import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Coins, AlertCircle, TrendingUp, Send, RefreshCw, Hourglass, Plus } from 'lucide-react';
import BuyCreditsDialog from '../../components/BuyCreditsDialog';

/**
 * Client Credits — a partner gives their own clients credits, and sees where each one stands.
 *
 * Buying is self-serve: the partner pays through Razorpay and the platform books the wallet
 * from the signed webhook. Everything after the money is here too, so nobody has to be asked
 * to move credits from a balance that is already the partner's.
 *
 * The price shown when buying is THIS partner's — their negotiated floor and their tier's
 * discount, fetched from the platform. Never compute it in the browser; a reseller signed at
 * a lower floor would be quoted the published one.
 *
 * Two things on this screen are deliberate and easy to undo by accident:
 *
 *   Lapsed credits are their own tile, never folded into the balance. A partner who let
 *   credits expire is owed the sight of it — hiding it is how the number grows.
 *
 *   A client at zero is rendered loud, because it is not a warning: that client's product
 *   is switched off right now, and this screen is the only place their partner finds out.
 */
const PartnerCredits = () => {
  const { getAuthHeaders, API_URL, user } = useAuth();
  const [balance, setBalance] = useState(null);
  const [clients, setClients] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreachable, setUnreachable] = useState(null);

  const [target, setTarget] = useState(null);   // the client being funded
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  const [invites, setInvites] = useState([]);
  const [buying, setBuying] = useState(false);

  const load = useCallback(async () => {
    try {
      const [bal, cli, led, inv] = await Promise.all([
        axios.get(`${API_URL}/api/partner/credits`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/partner/credits/clients`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/partner/credits/ledger?limit=15`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/partner/client-invites`, { headers: getAuthHeaders() }),
      ]);
      setBalance(bal.data);
      setClients(cli.data.clients || []);
      setLedger(led.data.transactions || []);
      setInvites(inv.data.invites || []);
      setUnreachable(null);
    } catch (error) {
      // A 503 here means Expedify could not be reached, not that the partner has nothing.
      // An empty screen would read as "you have no credits", which is a different and
      // much worse sentence than "we could not check".
      const detail = error?.response?.data?.detail;
      setUnreachable(
        error?.response?.status === 503
          ? (detail || 'Your credit balance could not be read right now.')
          : null
      );
      if (error?.response?.status !== 503) toast.error('Failed to load your credits');
    } finally {
      setLoading(false);
    }
  }, [API_URL, getAuthHeaders]);

  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const allocate = async () => {
    const credits = Number(amount);
    if (!credits || credits <= 0) {
      toast.error('Enter how many credits to give');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/partner/credits/allocate`,
        { org_id: target.org_id, credits, note: note || null },
        { headers: getAuthHeaders() }
      );
      toast.success(
        `${formatCredits(credits)} credits sent to ${target.name}. ` +
        `They now hold ${formatCredits(res.data.org_credits_balance)}.`
      );
      setTarget(null);
      setAmount('');
      setNote('');
      // Refresh straight away. The balance that funded this and the client that received it
      // both just changed, and a partner should not have to reload to believe it happened.
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not give those credits');
    } finally {
      setSending(false);
    }
  };

  const copyLink = async (url, message) => {
    if (!url) {
      toast.error('This invite has no link yet — tell Expedify that MAIN_APP_URL is unset.');
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success(message || 'Link copied.');
    } catch {
      // Clipboard is blocked on http:// origins and in some embedded browsers. Showing the
      // link is worse than copying it and better than losing it.
      toast.success(url, { duration: 15000 });
    }
  };

  const revokeInvite = async (invite) => {
    try {
      await axios.post(
        `${API_URL}/api/partner/client-invites/${invite.id}/revoke`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('That link will no longer work.');
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not cancel that invite');
    }
  };

  const pendingInvites = invites.filter((i) => i.status === 'pending');

  const formatCredits = (n) =>
    new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n || 0);

  const formatDate = (s) => {
    if (!s) return '—';
    return new Date(s).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const switchedOff = clients.filter((c) => c.credit_status === 'no_credits');
  const runningLow = clients.filter((c) => c.credit_status === 'low');

  /**
   * Whether this client is paid for THIS QUARTER, which is a different question from how
   * many credits they have left.
   *
   * A client can be sitting on plenty and still be about to stop: credits lapse when they
   * fall outside the funding window, so what keeps a client alive is money arriving, not
   * money remaining. Showing only the balance is how a partner gets surprised.
   *
   * The shortfall is spelled out rather than left as "not funded", because the partner's
   * next action is to give a specific number of credits and they should not have to work
   * it out.
   */
  const FundingState = ({ client }) => {
    const required = client.funding_required || 0;
    const received = client.received_in_window || 0;
    if (!required) return <span className="text-[#64748B]">—</span>;
    if (client.is_funded) {
      return (
        <span className="text-[#64748B]">
          {formatCredits(received)} given
        </span>
      );
    }
    return (
      <span className="text-amber-700 font-medium" title="Credits given inside the current window">
        {formatCredits(required - received)} more needed
      </span>
    );
  };

  const CreditStatus = ({ client }) => {
    if (client.credit_status === 'no_credits') {
      return (
        <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
          <AlertCircle className="h-3 w-3" /> Switched off
        </span>
      );
    }
    if (client.credit_status === 'low') {
      return (
        <span className="rounded px-2 py-0.5 text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
          Running low
        </span>
      );
    }
    return (
      <span className="rounded px-2 py-0.5 text-xs font-medium bg-green-50 text-green-700">
        Running
      </span>
    );
  };

  return (
    <DashboardLayout>
      <BuyCreditsDialog
        open={buying}
        onOpenChange={setBuying}
        /* The webhook books the wallet a moment after the payment window closes, so reload
           rather than assume — the balance shown is always one we have actually read. */
        onPaid={load}
        partnerName={user?.full_name}
        partnerEmail={user?.email}
      />
      <div className="space-y-6" data-testid="partner-credits">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A1A]">Client Credits</h1>
            <p className="text-sm text-[#64748B] mt-1">
              Give credits from your balance to the clients you bill
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="border-[#E2E2DF]"
              onClick={load}
              data-testid="refresh-credits"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button
              className="bg-[#ef785b] hover:bg-[#D44017] text-white"
              onClick={() => setBuying(true)}
              data-testid="open-buy-credits"
            >
              <Plus className="w-4 h-4 mr-2" /> Buy credits
            </Button>
          </div>
        </div>

        {unreachable && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3"
               data-testid="credits-unreachable">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <p className="text-xs text-[#64748B]">
              <span className="font-semibold text-[#1A1A1A]">{unreachable}</span>{' '}
              Nothing has changed — this is a connection problem at our end. Try again shortly.
            </p>
          </div>
        )}

        {/* Balance */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#ef785b]/10 flex items-center justify-center">
                  <Coins className="w-5 h-5 text-[#ef785b]" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Credits to Give</p>
                  <p className="text-xl font-bold text-[#1A1A1A]" data-testid="credits-available">
                    {formatCredits(balance?.credits_available)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Credits Bought</p>
                  <p className="text-xl font-bold text-[#1A1A1A]" data-testid="credits-purchased">
                    {formatCredits(balance?.credits_purchased)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Its own tile, never folded into the balance above. See the file header. */}
          <Card className={`border ${balance?.credits_lapsed > 0
            ? 'bg-amber-50 border-amber-200' : 'bg-white border-[#E2E2DF]'}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  balance?.credits_lapsed > 0 ? 'bg-amber-100' : 'bg-[#FAFAF8]'}`}>
                  <Hourglass className={`w-5 h-5 ${
                    balance?.credits_lapsed > 0 ? 'text-amber-600' : 'text-[#64748B]'}`} />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Expired Unused</p>
                  <p className="text-xl font-bold text-[#1A1A1A]" data-testid="credits-lapsed">
                    {formatCredits(balance?.credits_lapsed)}
                  </p>
                </div>
              </div>
              {balance?.credits_lapsed > 0 && (
                <p className="text-xs text-[#64748B] mt-2">
                  Credits expire twelve months after purchase and are not refunded.
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* The clients who are stopped right now. Louder than the table, because a partner
            reading this screen at a glance must not miss it. */}
        {switchedOff.length > 0 && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3"
               data-testid="clients-switched-off">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
            <p className="text-xs text-[#64748B]">
              <span className="font-semibold text-[#1A1A1A]">
                {switchedOff.length === 1
                  ? `${switchedOff[0].name} has no credits left and is switched off right now.`
                  : `${switchedOff.length} of your clients have no credits left and are switched off right now.`}
              </span>{' '}
              Give them credits below to start them again.
            </p>
          </div>
        )}

        {switchedOff.length === 0 && runningLow.length > 0 && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3"
               data-testid="clients-running-low">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <p className="text-xs text-[#64748B]">
              <span className="font-semibold text-[#1A1A1A]">
                {runningLow.length === 1
                  ? `${runningLow[0].name} is running low on credits.`
                  : `${runningLow.length} of your clients are running low on credits.`}
              </span>{' '}
              Top them up before they stop.
            </p>
          </div>
        )}

        {/* Invites already sent, and not yet taken up.
            Creating one lives on Invite, with every other way of bringing a client in; this
            page owns the other half — cancelling one, and topping a client up — which is the
            split Invite's own footnote promises. There used to be a second, complete copy of
            the create flow here, next to a sentence saying creating lived on Invite. Two
            implementations of one thing is one that quietly falls behind: this one carried a
            cost disclosure the surviving one did not, which has moved across with it. */}
        {pendingInvites.length > 0 && (
        <Card className="bg-white border-[#E2E2DF]">
          <CardContent className="p-4">
            <div>
              <p className="font-semibold text-[#1A1A1A]">Invites awaiting signup</p>
              <p className="text-xs text-[#64748B] mt-1">
                Each lands the client on your credits with their first{' '}
                {formatCredits(balance?.min_client_seed)} already given. Nothing leaves your
                balance until they sign up. Need another?{' '}
                <a href="/invite" className="underline">Invite</a>.
              </p>
            </div>

            {pendingInvites.length > 0 && (
              <div className="mt-4 border-t border-[#E2E2DF] pt-3 space-y-2">
                {pendingInvites.map((invite) => (
                  <div
                    key={invite.id}
                    className="flex flex-wrap items-center justify-between gap-2 text-xs"
                    data-testid={`invite-${invite.id}`}
                  >
                    <div className="text-[#64748B]">
                      <span className="font-medium text-[#1A1A1A]">
                        {invite.client_name || invite.client_email || 'Unnamed client'}
                      </span>
                      {' · '}sent {formatDate(invite.created_at)}
                      {' · '}expires {formatDate(invite.expires_at)}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm" variant="outline" className="border-[#E2E2DF] h-7 text-xs"
                        onClick={() => copyLink(invite.signup_url)}
                      >
                        Copy link
                      </Button>
                      <Button
                        size="sm" variant="ghost" className="h-7 text-xs text-[#64748B]"
                        onClick={() => revokeInvite(invite)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Clients */}
        <Card className="bg-white border-[#E2E2DF] overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FAFAF8] hover:bg-[#FAFAF8]">
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Client</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Credits Left</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">This Quarter</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Used (30 days)</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Given in Total</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#ef785b]"></div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : clients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-[#64748B]">
                      No clients are on your credit arrangement yet. Create an invite link on{' '}
                      <a href="/invite" className="underline">Invite</a> and send it to your
                      first one.
                    </TableCell>
                  </TableRow>
                ) : (
                  clients.map((client, index) => (
                    <TableRow
                      key={client.org_id}
                      className={client.credit_status === 'no_credits'
                        ? 'bg-red-50/50 hover:bg-red-50'
                        : 'hover:bg-[#FAFAF8]/50'}
                      data-testid={`credit-client-${index}`}
                    >
                      <TableCell className="font-medium text-[#1A1A1A]">
                        {client.name}
                      </TableCell>
                      <TableCell><CreditStatus client={client} /></TableCell>
                      <TableCell className="font-medium text-[#1A1A1A]">
                        {formatCredits(client.credits_balance)}
                      </TableCell>
                      <TableCell className="text-sm"><FundingState client={client} /></TableCell>
                      <TableCell className="text-sm text-[#64748B]">
                        {formatCredits(client.consumed_last_30d)}
                      </TableCell>
                      <TableCell className="text-sm text-[#64748B]">
                        {formatCredits(client.net_allocated)}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          className="bg-[#ef785b] hover:bg-[#D44017] text-white"
                          onClick={() => { setTarget(client); setAmount(''); setNote(''); }}
                          data-testid={`allocate-btn-${index}`}
                        >
                          <Send className="w-3.5 h-3.5 mr-1.5" /> Give Credits
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>

        {/* Recent movements */}
        {ledger.length > 0 && (
          <Card className="bg-white border-[#E2E2DF] overflow-hidden">
            <CardContent className="p-4">
              <p className="text-xs text-[#64748B] uppercase tracking-wider mb-3">Recent Activity</p>
              <div className="space-y-2">
                {ledger.map((t) => (
                  <div key={t.id} className="flex items-center justify-between text-sm border-b border-[#F1F1EE] pb-2 last:border-0">
                    <div>
                      <span className="text-[#1A1A1A]">{t.note || t.kind}</span>
                      <span className="text-xs text-[#64748B] ml-2">{formatDate(t.created_at)}</span>
                    </div>
                    <span className={`font-medium ${t.credits < 0 ? 'text-[#64748B]' : 'text-green-600'}`}>
                      {t.credits < 0 ? '' : '+'}{formatCredits(t.credits)}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Give credits */}
        <Dialog open={!!target} onOpenChange={() => !sending && setTarget(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Give credits to {target?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="rounded-lg bg-[#FAFAF8] p-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#64748B]">They hold now</span>
                  <span className="font-medium text-[#1A1A1A]">
                    {formatCredits(target?.credits_balance)}
                  </span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-[#64748B]">You have to give</span>
                  <span className="font-medium text-[#1A1A1A]">
                    {formatCredits(balance?.credits_available)}
                  </span>
                </div>
              </div>
              <div>
                <label className="text-xs text-[#64748B] uppercase tracking-wider">Credits</label>
                <Input
                  type="number"
                  min="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="5000"
                  className="h-10 border-[#E2E2DF] mt-1"
                  data-testid="allocate-amount"
                />
              </div>
              <div>
                <label className="text-xs text-[#64748B] uppercase tracking-wider">
                  Note (optional)
                </label>
                <Input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="August top-up"
                  className="h-10 border-[#E2E2DF] mt-1"
                  data-testid="allocate-note"
                />
              </div>
              <p className="text-xs text-[#64748B]">
                Credits leave your balance and land with {target?.name} straight away. Your
                oldest credits are used first, so nothing expires unused.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" className="border-[#E2E2DF]"
                      onClick={() => setTarget(null)} disabled={sending}>
                Cancel
              </Button>
              <Button
                className="bg-[#ef785b] hover:bg-[#D44017] text-white"
                onClick={allocate}
                disabled={sending}
                data-testid="confirm-allocate"
              >
                {sending ? 'Sending…' : 'Give Credits'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </DashboardLayout>
  );
};

export default PartnerCredits;
